import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { CompaniesGateway, CompaniesPage } from '../infrastructure/companies.gateway';
import { companiesRequested, pageChanged, pageSizeChanged } from './companies.actions';
import { selectPage, selectPageSize } from './companies.selectors';
import { companiesActions } from './companies.slice';

const LOAD_ERROR = 'Failed to load companies.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : LOAD_ERROR;
}

/**
 * WORKER: fetch the current page. Reads `page` + `pageSize` back from the store
 * (the single source of truth for the fetch) and calls the gateway. `takeLatest`
 * cancels an in-flight fetch when a newer trigger arrives; the abort-latest
 * behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: CompaniesGateway = yield getContext('companiesGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);

  yield put(companiesActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchCompanies, { page, pageSize })) as CompaniesPage;
    yield put(companiesActions.loadSucceeded(result));
  } catch (e) {
    yield put(companiesActions.loadFailed(toMessage(e)));
  }
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(companiesActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(companiesActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/** WATCHERS. */
export function* companiesSaga() {
  yield all([
    takeLatest(companiesRequested.type, fetchCurrentPage),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
